import orderBy from 'lodash/orderBy';
import React, { useCallback, useMemo, useState } from 'react';
import {
  AddProgramInput,
  ProgramAttributes,
  ProgramStatus,
  UpdateProgramInput,
} from '@loyalty/shared-common/gamification-api-client';
import { Card, CardActionArea, Grid, Theme, Tooltip } from '@mui/material';
import { Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { colorPalette, useAlerts } from '@qualifio/ui-commons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';
import { makeStyles } from '@mui/styles';
import { useNavigate } from '@qualifio/ui-commons/dist/components/qlf-prompt';
import { useTranslation } from 'react-i18next';
import { LoyaltyItem } from './components/loyalty-item';
import { LoyaltyProgramDialog } from './components/dialog';
import { LoyaltyProgramEmbedDialog } from './components/embed';
import { PageTitle } from '../shared/page';
import { Router } from '../services';
import { useAccounts } from '../accounts/AccountsContext';
import { useApi } from '../shared/api-client/ApiClientProvider';
import { useFeatures } from '../features/FeaturesContext';
import { usePrograms } from './ProgramsContext';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  root: {
    padding: spacing(2.5),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  list: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: spacing(-1),
  },
  listItem: {
    height: 330,
    margin: spacing(1),
    width: 275,
  },
  listItemContent: {
    padding: spacing(0.5, 1),
    '&:last-child': {
      paddingBottom: spacing(0.5),
    },
  },
  newItem: {
    backgroundColor: `${colorPalette.grey.main} !important`,
    alignItems: 'center',
    color: colorPalette.grey.dark,
    display: 'flex',
    justifyContent: 'center',
    height: '100%',
  },
}));

function isDeleting(program: ProgramAttributes): boolean {
  return (
    program.status === ProgramStatus.DELETING || program.status === ProgramStatus.DELETING_ERROR
  );
}

export function Loyalty(): React.ReactElement {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notify } = useAlerts();
  const { features } = useFeatures();
  const { write } = features[Feature.PROGRAM];
  const { enabled: seeProgramDeletion } = features[Feature.SEE_PROGRAM_DELETION];

  const { currentAccount } = useAccounts();
  const { programs, updateProgram, getPrograms, addProgram } = usePrograms();
  const accountId = currentAccount.id;

  const [programDialogVisible, setProgramDialogVisible] = useState(false);
  const [withEmbedDialog, setEmbedDialogVisible] = useState(false);
  const [dialogProgram, setDialogProgram] = useState<ProgramAttributes | undefined>(undefined);

  const sortedLoyaltyPrograms = useMemo(
    () => orderBy<ProgramAttributes>(programs, [program => program.name.toLowerCase()], ['asc']),
    [programs],
  );

  const { gamificationApi, managerApi } = useApi();

  const openDialog = useCallback(
    (program: ProgramAttributes | undefined = undefined) =>
      () => {
        setProgramDialogVisible(true);
        setDialogProgram(program);
      },
    [],
  );
  const handleDialogClosed = useCallback(() => setDialogProgram(undefined), []);
  const handleProgramDialogClose = useCallback(() => setProgramDialogVisible(false), []);
  const handleEmbedDialogClose = useCallback(() => setEmbedDialogVisible(false), []);

  const handleSave = useCallback(
    async (values: AddProgramInput | UpdateProgramInput) => {
      const freshProgram = dialogProgram?.id
        ? await updateProgram(dialogProgram.id, values)
        : await addProgram(values as AddProgramInput);

      await getPrograms();
      if (!dialogProgram?.id && freshProgram?.id) {
        const programPath = Router.getPath('loyaltyProgram', {
          accountId,
          id: freshProgram.id,
        });
        navigate(programPath);
      } else {
        handleProgramDialogClose();
      }
    },
    [addProgram, updateProgram, accountId, dialogProgram, handleProgramDialogClose, navigate],
  );

  const handleEmbed = useCallback(
    (program: ProgramAttributes) => () => {
      setEmbedDialogVisible(true);
      setDialogProgram(program);
    },
    [],
  );

  return (
    <div className={classes.root}>
      <PageTitle>{t('loyalty.title')}</PageTitle>

      <div className={classes.list}>
        <Card className={classes.listItem}>
          <CardActionArea className={classes.newItem} onClick={openDialog()} id={'new-program-id'}>
            <Tooltip title={t('loyalty.tooltips.add')} placement="top">
              <Grid container justifyContent="center">
                <FontAwesomeIcon icon={faPlus} size="2x" color={colorPalette.grey.dark} />
              </Grid>
            </Tooltip>
          </CardActionArea>
        </Card>

        {sortedLoyaltyPrograms
          .filter(program => !isDeleting(program) || seeProgramDeletion)
          .map(loyaltyProgram => (
            <LoyaltyItem
              key={loyaltyProgram.id}
              loyaltyProgram={loyaltyProgram}
              onEdit={openDialog(loyaltyProgram)}
              onEmbed={handleEmbed(loyaltyProgram)}
            />
          ))}
      </div>

      {programDialogVisible && (
        <LoyaltyProgramDialog
          enabled={write}
          open={true}
          account={currentAccount}
          program={dialogProgram}
          onSubmit={handleSave}
          onClose={handleProgramDialogClose}
          TransitionProps={{
            onExited: handleDialogClosed,
          }}
        />
      )}

      <LoyaltyProgramEmbedDialog
        accountId={accountId}
        api={managerApi}
        open={withEmbedDialog}
        program={dialogProgram}
        onClose={handleEmbedDialogClose}
        TransitionProps={{
          onExited: handleDialogClosed,
        }}
      />
    </div>
  );
}
